import { storeWrapper } from '../storeWrapper'
import { debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { handleFormEvoTokenExpired } from '@repo/utils/handleFormEvoTokenExpired'

const initialMappedFormData = {
  mappedFormId: null,
  mappingType: 'global',
  workTypes: [],
  formId: null,
  fieldMappings: []
}

const initialState = {
  formEvoCategories: [],
  selectedFormEvoCategory: null,
  mappedFormData: initialMappedFormData,
  matterDocumentsTableData: { rows: [], totalRowCount: 0 },
  matterDocumentsTableLoading: false
}

export const useThirdPartyFormsStore = storeWrapper('useThirdPartyFormsStore', (set, get) => ({
  ...initialState,
  setMappedFormData: ({ mappedFormData }) =>
    set({ mappedFormData: { ...get().mappedFormData, ...mappedFormData } }),
  resetMappedFormData: () => set({ mappedFormData: initialMappedFormData }),
  getFormEvoLoginUrl: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'getFormEvoLoginUrl',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: 'FormEvo/GetLoginUrl'
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching FormEvo login URL:', error)
            reject(error)
          }
        }
      })
    })
  },
  getFormEvoToken: async (code, state) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'getFormEvoToken',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `FormEvo/GetToken`,
              data: {
                code: code,
                state: state
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching FormEvo token:', error)
            reject(error)
          }
        }
      })
    })
  },
  hasFormEvoAccessTokenByCurrentUser: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'hasFormEvoAccessTokenByCurrentUser',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `FormEvo/HasFormEvoAccessTokenByCurrentUser`
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error checking FormEvo access token:', error)
            reject(error)
          }
        }
      })
    })
  },
  refreshFormsAndFormCategories: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'refreshFormsAndFormCategories',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'matters',
              endpoint: `FormEvo/RefreshFormsAndFormCategories`
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error refreshing forms and form categories:', error)
            reject(error)
          }
        }
      })
    })
  },
  getAllNestedFormEvoCategories: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'getAllNestedFormEvoCategories',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `FormCategory/GetAllNested`
            })

            const formEvoCategories = Array.isArray(response) ? response : []

            set({ formEvoCategories })

            resolve(formEvoCategories)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching nested FormEvo categories:', error)
            reject(error)
          }
        }
      })
    })
  },
  setSelectedFormEvoCategory: (selectedFormEvoCategory) => set({ selectedFormEvoCategory }),
  createMatterDocument: async ({
    matterDocumentId,
    clientNo,
    matterNo,
    formId,
    matterHistoryContentId,
    providerDocumentId,
    status,
    createdDate,
    createdBy,
    updatedDate,
    updatedBy
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'createMatterDocument',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'matters',
              endpoint: `MatterDocument/Create`,
              data: {
                matterDocumentId: matterDocumentId ?? null,
                clientNo: clientNo ?? null,
                matterNo: matterNo ?? null,
                formId: formId ?? null,
                matterHistoryContentId: matterHistoryContentId ?? null,
                providerDocumentId: providerDocumentId ?? null,
                status: status ?? null,
                createdDate: createdDate ?? null,
                createdBy: createdBy ?? null,
                updatedDate: updatedDate ?? null,
                updatedBy: updatedBy ?? null
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error creating matter document:', error)
            reject(error)
          }
        }
      })
    })
  },
  updateMatterDocument: async ({
    matterDocumentId,
    clientNo,
    matterNo,
    formId,
    matterHistoryContentId,
    providerDocumentId,
    status,
    createdDate,
    createdBy,
    updatedDate,
    updatedBy
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'updateMatterDocument',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'matters',
              endpoint: `MatterDocument/Update`,
              data: {
                matterDocumentId: matterDocumentId ?? null,
                clientNo: clientNo ?? null,
                matterNo: matterNo ?? null,
                formId: formId ?? null,
                matterHistoryContentId: matterHistoryContentId ?? null,
                providerDocumentId: providerDocumentId ?? null,
                status: status ?? null,
                createdDate: createdDate ?? null,
                createdBy: createdBy ?? null,
                updatedDate: updatedDate ?? null,
                updatedBy: updatedBy ?? null
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error updating matter document:', error)
            reject(error)
          }
        }
      })
    })
  },
  deleteMatterDocument: async ({ matterDocumentId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'deleteMatterDocument',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'DELETE',
              service: 'matters',
              endpoint: `MatterDocument/Delete`,
              data: {
                id: matterDocumentId ?? null
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error deleting matter document:', error)
            reject(error)
          }
        }
      })
    })
  },
  getAllExtendedMatterDocumentsBySearchCriteria: async ({
    startAtIndex,
    pageSize,
    clientNo,
    matterNo,
    formName,
    formDescription,
    auditReferenceNumber,
    createdBy,
    date,
    dateComparisonOperator,
    statuses,
    orderByColumns
  }) => {
    set({ matterDocumentsTableLoading: true })
    return new Promise((resolve, reject) => {
      debounce({
        id: 'getAllExtendedMatterDocumentsBySearchCriteria',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'matters',
              endpoint: `MatterDocument/GetAllExtendedBySearchCriteria`,
              data: {
                startAtIndex: startAtIndex ?? 0,
                pageSize: pageSize ?? 10,
                clientNo: clientNo ?? null,
                matterNo: matterNo ?? null,
                formName: formName ?? null,
                formDescription: formDescription ?? null,
                auditReferenceNumber: auditReferenceNumber ?? null,
                createdBy: createdBy ?? null,
                updatedDate: {
                  date: date ?? null,
                  dateComparisonOperator: dateComparisonOperator ?? 0
                },
                statuses: statuses ?? null,
                orderByColumns: orderByColumns ?? null
              }
            })

            set({
              matterDocumentsTableData: {
                rows: response?.rows ?? [],
                totalRowCount: response?.totalRowCount ?? 0
              },
              matterDocumentsTableLoading: false
            })

            resolve(response)
          } catch (error) {
            set({ matterDocumentsTableLoading: false })
            handleFormEvoTokenExpired(error)
            console.error('Error fetching extended matter documents by search criteria:', error)
            reject(error)
          }
        }
      })
    })
  },
  addDocumentToMatterHistory: async ({ matterDocumentId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'addDocumentToMatterHistory',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'matters',
              endpoint: `MatterDocument/AddDocumentToMatterHistory`,
              data: {
                matterDocumentId: matterDocumentId ?? null
              },
              sendAsUrlParams: true,
              successToast: {
                type: 'success',
                title: 'Success',
                text: 'Document added to matter history'
              },
              errorToast: {
                type: 'error',
                title: 'Error',
                text: 'Failed to add document to matter history'
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error adding document to matter history:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchDocumentContent: async ({ matterDocumentId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'fetchDocumentContent',
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `MatterDocument/GetDocumentContent`,
              data: {
                matterDocumentId: matterDocumentId ?? null
              },
              responseType: 'blob'
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching document content:', error)
            reject(error)
          }
        }
      })
    })
  },
  resetMatterDocumentsTable: () =>
    set({ matterDocumentsTableData: { rows: [], totalRowCount: 0 }, matterDocumentsTableLoading: false }),
  reset: () => set(initialState)
}))
